/**
 * Ten codes, shown once, and the one screen that has to make somebody act.
 *
 * `plan-documents/DECISION_recovery_and_bank_connections_20260919.md` §3 is the
 * decision. The operator's worry was exact and it is the whole design problem:
 * **a person who skips past these has no second way in and will not know it for
 * months.**
 *
 * A checkbox does not fix that; people click checkboxes. What does is the
 * argument `cli/enroll-totp.ts` already makes about the TOTP secret itself --
 * *"Prove the operator can actually generate codes before anything is saved…
 * storing a secret the operator has not proved they can generate codes from is
 * how people lock themselves out."* So this asks for ONE NAMED CODE typed back.
 * Typing it proves the list left the screen for somewhere it can be read from.
 *
 * THE TEXT STATES THE CONSEQUENCE, NOT THE INSTRUCTION. The failure to design
 * against is not that "save these" goes unread -- it is that the cost is
 * invisible and arrives months later, on a day the person is already in
 * trouble. So the sentence says what happens without them.
 *
 * NOT AN `InfoNote`. That folds prose because a paragraph always on screen is
 * one a reader learns to skip -- an argument about screens visited repeatedly.
 * This one is visited once.
 *
 * CONTINUE STAYS PRESSABLE and answers with a sentence when the typed code is
 * wrong. A disabled button and a handler that returns early say the same
 * nothing, and "I press it and nothing happens" cost a session on 2026-09-17.
 */

import { useState } from 'react';

/** Which one to ask for. Fixed rather than random so a retry asks the same one. */
const ASK_FOR_INDEX = 3;

const ORDINALS = [
  'first', 'second', 'third', 'fourth', 'fifth',
  'sixth', 'seventh', 'eighth', 'ninth', 'tenth',
];

export interface RecoveryCodesProps {
  codes: string[];
  /** Called once the person has typed one of the codes back. */
  onDone: () => void;
  /**
   * What produced these. Changes only the heading's lead-in.
   *
   * `created` is signup's: a new business and a new sign-in, with nothing
   * replaced. Signup borrowed `enrolled` until 2026-10-10, and its "Your
   * authenticator has been moved" told the operator, signing up a second
   * business under a new address, that the FIRST business's authenticator had
   * changed. Nothing had; the sentence was Settings'.
   */
  reason: 'reissued' | 'enrolled' | 'created' | 'joined';
}

export function RecoveryCodes({ codes, onDone, reason }: RecoveryCodesProps) {
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  const index = Math.min(ASK_FOR_INDEX, codes.length - 1);
  const ordinal = ORDINALS[index] ?? 'first';

  const download = () => {
    const blob = new Blob(
      [
        'Techne Books — recovery codes\n',
        `Issued ${new Date().toISOString().slice(0, 10)}\n\n`,
        'Each recovery code signs you in once if you lose your authenticator.\n',
        'Keep this file somewhere only you can reach.\n\n',
        ...codes.map((code) => `  ${code}\n`),
      ],
      { type: 'text/plain' },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'techne-books-recovery-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  const submit = () => {
    const wanted = codes[index]!.trim().toUpperCase();
    if (typed.trim().toUpperCase() !== wanted) {
      setError(`That is not the ${ordinal} recovery code. Check the list above.`);
      return;
    }
    onDone();
  };

  return (
    <div className="recovery-codes" role="group" aria-label="Save These Recovery Codes">
      <h3>Save These Recovery Codes</h3>

      <p>
        {reason === 'created'
          ? 'Your business is set up, with a new sign-in for this address. These are its recovery codes; any other sign-in you have keeps its own.'
          : reason === 'joined'
            ? 'You have joined the business, with a new sign-in for this address. These are its recovery codes.'
            : reason === 'enrolled'
              ? 'Your authenticator has been moved, and these replace any recovery codes you had.'
              : 'These replace any recovery codes you had. The old ones no longer work.'}{' '}
        Each recovery code signs you in once if you lose your authenticator. They are shown here and
        nowhere else — only scrambled copies are stored, so nobody can look them up for you.{' '}
        <strong>
          Without a code and without your authenticator, this account and its books cannot be
          reached again.
        </strong>
      </p>

      <ol className="code-list">
        {codes.map((code) => (
          <li key={code}>
            <code>{code}</code>
          </li>
        ))}
      </ol>

      {/*
        * In its own block. Inline, it ran straight into the label beside it --
        * "Download ThemType the fourth code to continue" -- which the suite
        * cannot see and one screenshot showed immediately.
        */}
      <p>
        <button type="button" className="link" onClick={download}>
          Download Them
        </button>
      </p>

      <label className="field">
        <span>Type the {ordinal} recovery code to continue</span>
        <input
          value={typed}
          autoComplete="off"
          onChange={(event) => {
            // Read into a local before the updater: React may run a state
            // updater later than the event, and on a controlled input that
            // leaves typed text in the DOM while state stays empty.
            const next = event.target.value;
            setTyped(next);
            setError(null);
          }}
        />
      </label>

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      <button type="button" onClick={submit}>
        I Have Saved Them
      </button>
    </div>
  );
}
